/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { fg } from '@atlassian/jira-feature-gating';
import GlobalProductsSettingsSidebarQuery, {
	type GlobalProductsSettingsSidebarQuery as GlobalProductsSettingsSidebarQueryType,
} from './ui/__generated__/GlobalProductsSettingsSidebarQuery.graphql';

const SERVICE_DESK = 'jira-servicedesk';
const FREE_EDITION = 'FREE_EDITION';
const STANDARD_EDITION = 'STANDARD_EDITION';

const hasLicensedServiceDeskAboveStandard = ({
	licensedProducts,
	serviceDeskEdition,
}: {
	licensedProducts?: Record<string, boolean | undefined>;
	serviceDeskEdition?: string;
}) =>
	Boolean(licensedProducts?.[SERVICE_DESK]) &&
	serviceDeskEdition !== undefined &&
	serviceDeskEdition !== FREE_EDITION &&
	serviceDeskEdition !== STANDARD_EDITION;

type Queries = {
	nav4SidebarGlobalProductsSettings: GlobalProductsSettingsSidebarQueryType;
};

type Nav4SidebarGlobalProductsSettingsEntryPointComponent = EntryPointComponent<
	Queries,
	{},
	{},
	{}
>;

export const nav4SidebarGlobalProductsSettingsEntryPoint: EntryPoint<
	Nav4SidebarGlobalProductsSettingsEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	// sidebar is critical to rendering the initial view of a page
	root: JSResourceForUserVisible<Nav4SidebarGlobalProductsSettingsEntryPointComponent>(
		/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebars-settings-global-products" */ './ui/index.tsx',
		{
			includeInShellOnlySsrBundle: true,
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps({
		tenantContext: { cloudId, appEditions, isAdmin, licensedProducts },
	}: EntryPointRouteParams) {
		return {
			queries: {
				nav4SidebarGlobalProductsSettings: {
					parameters: GlobalProductsSettingsSidebarQuery,
					variables: {
						cloudId,
						// We can skip the permission check when:
						// 1. The user is an admin (because admins can automatically manage custom onboarding configs), or
						// 2. Upsell is enabled (because upsell is only available to admins), or
						// 3. The main custom onboarding hub gate is disabled (because the custom onboarding feature won't be visible to anyone)
						skipDelegatedAdminCheck:
							isAdmin ||
							appEditions?.software !== 'PREMIUM_EDITION' ||
							!fg('jira-onboarding-hub-gate'),
						skipJsmAdminCheck: isAdmin || !fg('enable_jsm_admin'),
						skipSiteWfoStatus: !(
							hasLicensedServiceDeskAboveStandard({
								licensedProducts,
								serviceDeskEdition: appEditions?.serviceDesk,
							}) && fg('jsm_wfo_skills')
						),
					},
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
				},
			},
		};
	},
});
